import axios from "axios";
import {API_BASE} from "../services/apirest";

const PUNTOS_VENTA_URL = `${API_BASE}/puntos_venta.php`;
const MATRIZ_PROMOCIONES_URL = `${API_BASE}/matriz_promociones.php`;
const ARCHIVOS_URL = `${API_BASE}/archivos.php`;

// Subir archivo
export const subirArchivo = async ({
                                       archivo,
                                       usuarioId,
                                       seccionId,
                                   }) => {
    if (!archivo) {
        throw new Error("No hay archivo para subir");
    }

    const formData = new FormData();
    formData.append("archivo", archivo);
    formData.append("usuarioId", usuarioId);
    formData.append("seccionId", seccionId);

    try {
        const response = await axios.post(ARCHIVOS_URL, formData, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        });

        // El backend puede responder con status:"success" en la raíz
        // O con result.status:"OK" (formato interno del backend)
        const data = response.data;
        const isOk =
            data?.status === "success" ||
            data?.result?.status === "OK";

        if (isOk) {
            const archivoData = data?.result?.archivo ?? data?.archivo;
            const nuevoArchivo = {
                nombre_archivo: archivoData?.nombre_archivo,
                ruta_archivo:   archivoData?.ruta_archivo,
                fecha_subida:   archivoData?.fecha_subida,
                tipo_mime:      archivoData?.tipo_mime,
                tamanio:        archivoData?.tamanio,
                id:             archivoData?.id,
            };
            return { success: true, archivo: nuevoArchivo };
        } else {
            const msg = data?.result?.error_msg ?? data?.mensaje ?? "Error desconocido";
            console.error(`Error al subir: ${msg}`);
            return { success: false, mensaje: msg };
        }
    } catch (error) {
        console.error("Error al subir el archivo:", error);
        return {success: false, mensaje: "Error al subir el archivo"};
    }
};

// Descargar archivos
// Función para descargar cualquier tipo de archivo
export const descargarArchivo = async (seccionId, nombreArchivo) => {
    try {
        if (!seccionId || isNaN(seccionId)) {
            throw new Error("El seccion_id proporcionado no es válido.");
        }

        const respuesta = await obtenerArchivos({ seccionId });
        if (!respuesta.success || !respuesta.archivos || respuesta.archivos.length === 0) {
            throw new Error("No se encontró ningún archivo para descargar.");
        }

        let archivoMeta;
        if (nombreArchivo) {
            archivoMeta = respuesta.archivos.find(
                (archivo) => archivo.nombre_archivo === nombreArchivo
            );
            if (!archivoMeta) {
                console.warn("No se encontró el archivo con el nombre especificado, se usará el primero.");
                archivoMeta = respuesta.archivos[0];
            }
        } else {
            archivoMeta = respuesta.archivos[0];
        }

        // Construye la URL para la descarga
        // Construye la URL para la descarga usando el nombre completo del archivo
        const url = `${API_BASE}/descargar_archivo.php?file=${encodeURIComponent(archivoMeta.nombre_completo)}`;


        console.log(archivoMeta.nombre_archivo);

        // Utiliza fetch para obtener el archivo como blob
        const response = await fetch(url);
        if (!response.ok) {
            throw new Error("Error al descargar el archivo desde el servidor.");
        }
        const blob = await response.blob();

        // Crea un URL temporal para el blob
        const blobUrl = window.URL.createObjectURL(blob);

        // Crea un elemento <a> y dispara la descarga
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = archivoMeta.nombre_archivo || `archivo_${seccionId}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        // Revoca el URL creado para liberar memoria
        window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
        console.error("Error al descargar el archivo:", error);
    }
};

// Obtener archivos por sección o por ID
export const obtenerArchivos = async ({
                                          seccionId = null,
                                          archivoId = null,
                                          archivado = false, // Nuevo parámetro con valor por defecto
                                      }) => {
    try {
        let url = ARCHIVOS_URL;
        const params = [];

        if (archivoId) {
            params.push(`archivo_id=${archivoId}`);
        } else if (seccionId) {
            params.push(`seccion_id=${seccionId}`);
        }

        // Se añade el parámetro 'archivado' siempre
        params.push(`archivado=${archivado}`);

        url += `?${params.join('&')}`;

        console.log(url);

        const response = await axios.get(url);

        if (
            response.data.status === "success" &&
            response.data.result &&
            response.data.result.status === "OK"
        ) {
            const files = response.data.result.archivos.map((archivo) => {
                const nombreCompleto = archivo.nombre_archivo;
                let nombreLimpio = nombreCompleto;
                const partes = nombreCompleto.split("_");
                if (partes.length > 2) {
                    nombreLimpio = partes.slice(2).join("_");
                }
                return {
                    nombre_archivo: nombreLimpio,
                    nombre_completo: nombreCompleto, // Nombre sin el prefijo
                    ruta_archivo: archivo.ruta_archivo,
                    fecha_subida: archivo.fecha_subida,
                    tipo_mime: archivo.tipo_mime,
                    tamanio: archivo.tamanio,
                    id: archivo.id,
                };
            });
            return { success: true, archivos: files };
        } else {
            const errorMsg = response.data.result?.error_msg || "Error desconocido";
            console.error(`Error: ${errorMsg}`);
            return { success: false, mensaje: errorMsg };
        }
    } catch (error) {
        console.error("Error al obtener los archivos:", error);
        return { success: false, mensaje: "Error al obtener los archivos" };
    }
};

// Eliminar archivo
export const eliminarArchivo = async ({archivoId}) => {
    try {
        const response = await axios.delete(ARCHIVOS_URL, {
            data: {archivo_id: archivoId},
        });

        if (response.data.status === "success") {
            return {success: true};
        } else {
            const errorMsg = response.data.result?.error_msg || "Error desconocido";
            console.error(`Error: ${errorMsg}`);
        }
    } catch (error) {
        console.error("Error al eliminar el archivo:", error);
        return {success: false, mensaje: "Error al eliminar el archivo"};
    }
};

// Obtener todos los puntos de venta o uno específico
export const obtenerPuntosVenta = async (id = null) => {
    try {
        const url = id ? `${PUNTOS_VENTA_URL}?id=${id}` : PUNTOS_VENTA_URL;
        const response = await axios.get(url);

        console.log(response)

        if (response.data.status === "success" &&
            response.data.result &&
            response.data.result.status === "OK") {
            return {success: true, puntosVenta: response.data.result.puntos_venta};
        } else {
            console.error(`Error: ${response.data.result.error_msg}`);
        }
    } catch (error) {
        console.error("Error al obtener los puntos de venta:", error);
        return {success: false, mensaje: "Error al obtener los puntos de venta"};
    }
};

export const obtenerEstadosPuntosVenta = async () => {
    try {
        // Usamos la función existente para obtener todos los puntos
        const result = await obtenerPuntosVenta();
        if (result.success && result.puntosVenta) {
            // Creamos un objeto para agrupar los puntos por estado
            // Usamos el estado como clave; de esta forma, se conservará el último (o el primero) de cada estado
            const estadosMap = {};
            result.puntosVenta.forEach((punto) => {
                // Se asigna el punto para ese estado (podrías elegir el primero o combinar datos si lo requieres)
                estadosMap[punto.estado] = punto;
            });
            // Convertimos el objeto a un array de puntos (cada uno representando un estado único)
            const estadosUnicos = Object.values(estadosMap);
            return {success: true, estados: estadosUnicos};
        } else {
            return result;
        }
    } catch (err) {
        console.error("Error en obtenerEstadosPuntosVenta:", err);
        return {success: false, error: err};
    }
};


// Agregar un nuevo punto de venta
export const agregarPuntoVenta = async (puntoVenta) => {
    try {
        const response = await axios.post(PUNTOS_VENTA_URL, puntoVenta, {
            headers: {"Content-Type": "application/json"},
        });

        if (response.data.status === "success") {
            return {success: true, id: response.data.id};
        } else {
            console.error(`Error: ${response.data.result.error_msg}`);
        }
    } catch (error) {
        console.error("Error al agregar el punto de venta:", error);
        return {success: false, mensaje: "Error al agregar el punto de venta"};
    }
};

// Actualizar un punto de venta existente
export const actualizarPuntoVenta = async (id, puntoVenta) => {
    try {
        const response = await axios.put(PUNTOS_VENTA_URL, {id, ...puntoVenta}, {
            headers: {"Content-Type": "application/json"},
        });

        if (response.data.status === "success") {
            return {success: true};
        } else {
            console.error(`Error: ${response.data.result.error_msg}`);
        }
    } catch (error) {
        console.error("Error al actualizar el punto de venta:", error);
        return {success: false, mensaje: "Error al actualizar el punto de venta"};
    }
};

// Eliminar un punto de venta
export const eliminarPuntoVenta = async (id) => {
    try {
        const response = await axios.delete(PUNTOS_VENTA_URL, {
            data: {id}, // Se envía el ID en el cuerpo de la solicitud
            headers: {"Content-Type": "application/json"},
        });

        if (response.data.status === "success") {
            return {success: true};
        } else {
            console.error(`Error: ${response.data.result.error_msg}`);
        }
    } catch (error) {
        console.error("Error al eliminar el punto de venta:", error);
        return {success: false, mensaje: "Error al eliminar el punto de venta"};
    }
};

//Archivar un archivo
export const archivarArchivo = async ({archivoId}) => {
    try {
        const response = await fetch(`${API_BASE}/archivarArchivo.php`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({archivoId}), // Solo se envía archivoId
        });
        const data = await response.json();
        return data;
    } catch (error) {
        console.error("Error en la conexión:", error);
        return {success: false, mensaje: "Error en la conexión."};
    }
};

//Matriz de promociones
export const obtenerMatrizPromociones = async (seccion_id = null) => {
    try {
        const url = seccion_id ? `${MATRIZ_PROMOCIONES_URL}?seccion_id=${seccion_id}` : MATRIZ_PROMOCIONES_URL;

        const response = await axios.get(url);

        if (response.data.status === "success") {
            return { success: true, matrizPromociones: response.data.result.matrizPromociones };
        } else {
            return { success: false, mensaje: response.data.result?.error_msg || "Error desconocido" };
        }
    } catch (error) {
        return { success: false, mensaje: "Error al obtener la matriz de promociones" };
    }
};

// Agregar una nueva promoción
export const agregarPromocion = async (promocion) => {
    try {
        const response = await axios.post(MATRIZ_PROMOCIONES_URL, {
            seccion_id: promocion.seccion_id,
            marca: promocion.marca,
            nombre_oferta: promocion.nombre_oferta,
            no_ac: promocion.no_ac,
            fecha_inicial: promocion.fecha_inicial,
            vigencia: promocion.vigencia,
            canales_aplica: promocion.canales_aplica,
            cobertura: promocion.cobertura,
            ...(promocion.pdfPromocion_id && { pdfPromocion_id: promocion.pdfPromocion_id }) // Solo si existe
        }, {
            headers: { "Content-Type": "application/json" },
        });

        if (response.data.status === "success") {
            return {success: true, id: response.data.id};
        } else {
            console.error(`Error: ${response.data.result.error_msg}`);
            return {success: false, mensaje: response.data.result.error_msg};
        }
    } catch (error) {
        console.error("Error al agregar la promoción:", error);
        return {success: false, mensaje: "Error al agregar la promoción"};
    }
};

// Actualizar una promoción existente
export const actualizarPromocion = async (id, promocion) => {
    try {
        const response = await axios.put(MATRIZ_PROMOCIONES_URL, { id, ...promocion }, {
            headers: { "Content-Type": "application/json" },
        });

        return response.data.status === "OK" ? { success: true } : { success: false, mensaje: response.data.result.error_msg };
    } catch (error) {
        return { success: false, mensaje: "Error al actualizar la promoción" };
    }
};

//obtener datos de news
export const obtenerNoticias = async () => {
    try {
        const response = await fetch(`${API_BASE}/news.php`);
        return await response.json();
    } catch (error) {
        console.error("Error al obtener noticias:", error);
        return { status: "ERROR", mensaje: "No se pudo obtener las noticias." };
    }
};

export const agregarNoticia = async (formData) => {
    try {
        const response = await fetch(`${API_BASE}/news.php`, {
            method: "POST",
            body: formData,
        });
        return await response.json();
    } catch (error) {
        console.error("Error al agregar noticia:", error);
        return { status: "error", error };
    }
};


export const actualizarNoticia = async (noticia) => {
    try {
        const response = await fetch(`${API_BASE}/news.php`, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify(noticia) // Asegurar que `id` está en el nivel principal
        });

        return await response.json();
    } catch (error) {
        console.error("Error al actualizar la noticia:", error);
        return { status: "error", message: "Error en la solicitud" };
    }
};

//eliminar Noticia
export const eliminarNoticia = async (id) => {
    try {
        const response = await fetch(`${API_BASE}/news.php`, {
            method: "DELETE",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ id }),
        });

        return await response.json();
    } catch (error) {
        console.error("Error al eliminar noticia:", error);
        return { status: "ERROR", message: "Error en la solicitud" };
    }
};

// Archivar una noticia (ocultarla sin eliminar)
export const archivarNoticia = async (id) => {
    try {
        const response = await fetch(`${API_BASE}/news.php`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ id, accion: "archivar" }),
        });
        return await response.json();
    } catch (error) {
        console.error("Error al archivar noticia:", error);
        return { status: "ERROR", message: "Error en la solicitud" };
    }
};

// Restaurar una noticia archivada
export const restaurarNoticia = async (id) => {
    try {
        const response = await fetch(`${API_BASE}/news.php`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ id, accion: "restaurar" }),
        });
        return await response.json();
    } catch (error) {
        console.error("Error al restaurar noticia:", error);
        return { status: "ERROR", message: "Error en la solicitud" };
    }
};


//subNews
// Función para obtener las subnews
export const obtenerSubNews = async () => {
    try {
        const response = await fetch(`${API_BASE}/subnews.php`, {
            method: "GET",
        });
        return await response.json();
    } catch (error) {
        console.error("Error al obtener subnews:", error);
        return { status: "error", error };
    }
};

// Función para agregar subnews (espera un array de registros)
export const agregarSubNews = async (formData) => {
    try {
        const response = await fetch(`${API_BASE}/subnews.php`, {
            method: "POST",
            body: formData, // Se envía el FormData, el navegador lo formatea
        });
        return await response.json();
    } catch (error) {
        console.error("Error al agregar subnews:", error);
        return { status: "error", error };
    }
};


// Función para actualizar una subnews
export const actualizarSubNews = async (formData) => {
    try {
        const response = await fetch(`${API_BASE}/subnews.php`, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify(formData),
        });

        return await response.json();
    } catch (error) {
        console.error("Error al actualizar subnews:", error);
        return { status: "error", error };
    }
};

// Función para eliminar una subnews
export const eliminarSubNews = async (id) => {
    try {
        const response = await fetch(`${API_BASE}/subnews.php`, {
            method: "DELETE",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ id }),
        });
        return await response.json();
    } catch (error) {
        console.error("Error al eliminar subnews:", error);
        return { status: "error", error };
    }
};

// Función para archivar una subnews
export const archivarSubNews = async (id) => {
    try {
        const response = await fetch(`${API_BASE}/subnews.php`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ id, accion: "archivar" }),
        });
        return await response.json();
    } catch (error) {
        console.error("Error al archivar subnews:", error);
        return { status: "error", error };
    }
};

// Función para restaurar una subnews
export const restaurarSubNews = async (id) => {
    try {
        const response = await fetch(`${API_BASE}/subnews.php`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ id, accion: "restaurar" }),
        });
        return await response.json();
    } catch (error) {
        console.error("Error al restaurar subnews:", error);
        return { status: "error", error };
    }
};






// Subir una nueva promoción a la matriz
export const subirMatrizPromocion = async (promocion) => {
    try {
        const response = await axios.post(MATRIZ_PROMOCIONES_URL, promocion, {
            headers: { "Content-Type": "application/json" },
        });

        if (response.data.status === "success") {
            return { success: true, id: response.data.id };
        } else {
            console.error(`Error: ${response.data.result?.error_msg || "Error desconocido"}`);
            return { success: false, mensaje: response.data.result?.error_msg || "Error desconocido" };
        }
    } catch (error) {
        console.error("Error al subir la promoción:", error);
        return { success: false, mensaje: "Error al subir la promoción" };
    }
};

// Actualizar una promoción existente en la matriz
export const actualizarMatrizPromocion = async (id, promocion) => {
    try {
        const response = await axios.put(MATRIZ_PROMOCIONES_URL, { id, ...promocion }, {
            headers: { "Content-Type": "application/json" },
        });

        if (response.data.status === "success") {
            return { success: true };
        } else {
            console.error(`Error: ${response.data.result?.error_msg || "Error desconocido"}`);
            return { success: false, mensaje: response.data.result?.error_msg || "Error desconocido" };
        }
    } catch (error) {
        console.error("Error al actualizar la promoción:", error);
        return { success: false, mensaje: "Error al actualizar la promoción" };
    }
};

// Eliminar una promoción de la matriz
export const eliminarMatrizPromocion = async (id) => {
    try {
        const response = await axios.delete(MATRIZ_PROMOCIONES_URL, {
            data: { id },
            headers: { "Content-Type": "application/json" },
        });

        if (response.data.status === "success") {
            return { success: true };
        } else {
            console.error(`Error: ${response.data.result?.error_msg || "Error desconocido"}`);
            return { success: false, mensaje: response.data.result?.error_msg || "Error desconocido" };
        }
    } catch (error) {
        console.error("Error al eliminar la promoción:", error);
        return { success: false, mensaje: "Error al eliminar la promoción" };
    }
};